import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  adminApprovalCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { adminEndpoints } from "@/features/admin/api/endpoints";
import type { ApprovalRequestDto } from "@/features/admin/api/dto";

/**
 * Annule une demande PENDING (`POST /ApprovalRequests/{id}/cancel`).
 * Réservé au demandeur d'origine : un autre appelant reçoit 403.
 * `requesterKeycloakId` part en query string (optionnel côté serveur).
 */
export const useCancelApprovalRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["admin", "approval-requests", "cancel"],
    mutationFn: ({
      approvalRequestId,
      requesterKeycloakId,
    }: {
      approvalRequestId: string;
      requesterKeycloakId?: string;
    }) => {
      const base = adminEndpoints.approvalRequests.cancel(approvalRequestId);
      const url = requesterKeycloakId
        ? `${base}?requesterKeycloakId=${encodeURIComponent(requesterKeycloakId)}`
        : base;
      return mutateData({ url, method: "POST" }) as Promise<ApprovalRequestDto>;
    },
    onSuccess: (_data, { approvalRequestId }) => {
      return invalidateAfterMutation(
        queryClient,
        adminApprovalCacheKeys(approvalRequestId)
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useCancelApprovalRequest failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "admin" }
      );
    },
  });
};
